import React, {
  useCallback, useEffect, useState, useMemo,
} from 'react';
import { AIMessage, EVENT_NAME_LIVE_AGENT_CHAT } from '@bingads-webui/ads-copilot-ui';
import { useI18nContext } from '@bingads-webui/copilot-i18n';
import { Spinner } from '@fluentui/react-components';
import { type Activity, type Message } from 'botframework-directlinejs';
import { type CopilotToggleCustomEventDetail, type ILogger, LiveAgentQueue } from '@bingads-webui-react/msa-copilot-common';
import { useLiveAgentStyles } from './styles';
import { LiveAgentQueueMenu } from './live-agent-queue-menu';
import { useLiveAgentCookies } from '../hooks';

interface ILiveAgentTransferProps {
  logger?: ILogger;
  activity: Activity;
  params: any;
  cardText?: string;
  locale?: string;
  isEnglishOnlyChatSupportAvailable?: boolean;
  onFeedbackButtonClick: any;
  onLiveAgentTransferClick: (activityId: string, queue?: LiveAgentQueue, problemDescription?: string) => void;
  isCardButtonDisabled: boolean;
  isIntelligentRoutingEnabled?: boolean;
  isAIDegraded?: boolean;
}

export const LiveAgentTransferCard: React.FC<ILiveAgentTransferProps> = ({
  logger,
  activity,
  params,
  locale,
  isEnglishOnlyChatSupportAvailable = false,
  cardText = _TL_('Need assistance from a support agent? Click the button below to chat with a real person.'),
  onLiveAgentTransferClick,
  onFeedbackButtonClick,
  isCardButtonDisabled,
  isIntelligentRoutingEnabled,
  isAIDegraded,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [hasSentImmediateTransferRequest, setHasSentImmediateTransferRequest] = useState<boolean>(false);
  const styles = useLiveAgentStyles();
  const i18n = useI18nContext();

  const {
    getLiveChatContext,
    clearLiveChatCookies,
  } = useLiveAgentCookies(logger);

  const isLiveAgentChatInitialized = params.currentActivities?.map((act: Message) => act.value).includes('LiveAgentMessage');
  const shouldShowSpinner = loading && !isLiveAgentChatInitialized;

  const connectToLiveAgent = useCallback((queue?: LiveAgentQueue) => {
    setLoading(true);
    onLiveAgentTransferClick?.(activity.id || '', queue);
  }, [activity, onLiveAgentTransferClick]);

  const onQueueSelected = useCallback((queue?: LiveAgentQueue) => {
    logger?.trace(`Copilot - Live Agent: Transfer card clicked - queue=${queue}, locale=${locale}, conversationId=${activity.conversation?.id}`);
    if (isEnglishOnlyChatSupportAvailable) {
      logger?.trace(`Copilot - Live Agent: Chat support in English for unsupported locale: ${locale}, queue: ${queue}`);
    }
    clearLiveChatCookies();
    connectToLiveAgent(queue);
  }, [activity, connectToLiveAgent, isEnglishOnlyChatSupportAvailable, locale]);

  useEffect(() => {
    const context = getLiveChatContext();
    if (context?.chatReconnectContext?.reconnectId) {
      if (context?.copilotConversationId === activity.conversation?.id) {
        logger?.trace(`Copilot - Live Agent: Auto restore conversation with conversationId: ${activity.conversation?.id}`);
        connectToLiveAgent();
      } else {
        logger?.trace(`Copilot - Live Agent: Auto restore conversation failed, existing conversationId: ${context?.copilotConversationId}, new conversationId: ${activity.conversation?.id}`);
        clearLiveChatCookies();
      }
    }
  }, []);

  const action = useMemo(() => (
    <LiveAgentQueueMenu
      locale={locale}
      isEnglishOnlyChatSupportAvailable={isEnglishOnlyChatSupportAvailable}
      disabled={isCardButtonDisabled}
      onQueueSelected={onQueueSelected}
      isIntelligentRoutingEnabled={isIntelligentRoutingEnabled}
    />
  ), [i18n, styles.singleButton, locale, isCardButtonDisabled, connectToLiveAgent]);

  const message = useMemo<Message>(() => {
    const unsupportedLocaleWithEnglishSupportText = _TL_('Please note that the live chat is only available in English in your country/region.');
    const customizedEnglishSupportTextForTaiwanHongKong = _TL_('Currently, the live chat is only available in English.<br /><br />If you\'d like to get the help from a Microsoft Advertising Expert in your language, please contact us by phone at {{phonePlaceholder}} during these hours:<br /><br />Mon-Fri: {{supportHrsMonFriplaceholder}}<br />Sat-Sun: Closed');
    const aiDegradeText = _TL_('We apologize for the inconvenience caused by our internal issue. If you need further assistance, please click the button below to connect with an agent.');

    const getText = () => {
      if (isEnglishOnlyChatSupportAvailable) {
        if (locale?.toLocaleLowerCase() === 'zh-tw') {
          return i18n?.getString(customizedEnglishSupportTextForTaiwanHongKong, { phonePlaceholder: '0800 888 384', supportHrsMonFriplaceholder: '9:00 AM-6:00 PM' });
        }
        if (locale?.toLocaleLowerCase() === 'zh-hk') {
          return i18n?.getString(customizedEnglishSupportTextForTaiwanHongKong, { phonePlaceholder: '3177-6688', supportHrsMonFriplaceholder: '9:00 AM-5:30 PM' });
        }
        return i18n?.getString(unsupportedLocaleWithEnglishSupportText);
      }
      if (isAIDegraded) {
        return i18n?.getString(aiDegradeText);
      }
      return i18n?.getString(cardText);
    };

    return {
      text: getText(),
      value: {
        StreamingStatus: 'Completed',
      },
    } as Message;
  }, [i18n, isAIDegraded, cardText, isEnglishOnlyChatSupportAvailable]);

  const liveAgentJwt = useMemo(() => activity?.channelData?.JwtTokens?.find(({ queue }: { queue: string }) => queue === LiveAgentQueue.General)?.token, [activity?.channelData?.JwtTokens]);

  const isImmediateTransferEventActivity = activity.type === 'event' && activity.name === EVENT_NAME_LIVE_AGENT_CHAT;
  useEffect(() => {
    if (!hasSentImmediateTransferRequest
      && isImmediateTransferEventActivity
      && !(activity as any).isFromRestore) {
      const eventDetail = (activity as any).value?.eventDetail as CopilotToggleCustomEventDetail;
      const { queue, problemDescription } = eventDetail?.eventData?.liveAgentParameters || {};

      // user has triggered a request to connect to a specific live agent queue, connect immediately
      logger?.trace(`Copilot - Live Agent: Immediately connecting to queue. locale=${locale}, queue=${queue}, problemDescription="${problemDescription}"`);
      setHasSentImmediateTransferRequest(true);
      onLiveAgentTransferClick(activity.id || '', queue, problemDescription);
    }
  }, [
    activity,
    logger,
    onLiveAgentTransferClick,
    locale,
    hasSentImmediateTransferRequest,
  ]);

  if (isImmediateTransferEventActivity) {
    // nothing to render
    return null;
  }

  return (
    <div id="live-agent-transfer-card" data-live-agent-jwt={liveAgentJwt} style={{ display: 'flex', float: 'left', flexDirection: 'column' }}>
      <AIMessage
        className="ai-message-live-agent"
        message={message}
        action={action}
        showReferences={false}
        showCopyButton={false}
        onFeedbackButtonClick={onFeedbackButtonClick}
      />
      {shouldShowSpinner && <Spinner size="extra-small" style={{ marginTop: 16 }} />}
    </div>
  );
};
